import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { ModalGameEditTabs } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  handleClearItem,
  getGamesItem,
  updateGame,
} from "store/gameCenter/games/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import GamesMainTab from "../tabs/games/GamesMainTab";
import GamesGeoTab from "../tabs/games/GamesGeoTab";
import GamesFSTab from "../tabs/games/GamesFSTab";
import GamesTrailTab from "../tabs/games/GamesTrailTab";
import SeoTab from "../tabs/SeoTab";

import { Modal } from "components/Modal/Modal";
import styles from "./GameCenterGamesEditModal.module.scss";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import lockIcon from "assets/icons/unlock-fill.svg";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import {
  getSeoFieldErrors,
  getChangedSeoAttributes,
  normalizeSeoHtml,
} from "helpers/seoAttributes";

const GameCenterGamesEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const payload = useAppSelector((store) => store.games.item);
  const isFetched = useAppSelector((store) => store.games.isItemFetched);
  const tabs: ModalGameEditTabs[] = [
    ModalGameEditTabs.main,
    ModalGameEditTabs.geo,
    ModalGameEditTabs.seo,
    ModalGameEditTabs.freeSpins,
    ModalGameEditTabs.trail,
  ];
  const [activeTab, setActiveTab] = useState<ModalGameEditTabs>(
    ModalGameEditTabs.main,
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isTabLoading, setIsTabLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>();
  const [initialSeoData, setInitialSeoData] = useState<any>();

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const [loadResult, setLoadResult] = useState<{
    id: string;
    failed: boolean;
  }>();
  const [loadAttempt, setLoadAttempt] = useState(0);

  const permissions = usePermissionHelper(
    location.pathname === "/dashboard"
      ? "/game-center/games/active"
      : undefined,
  );

  useEffect(() => {
    if (activeTab === ModalGameEditTabs.freeSpins && !payload?.hasFreeSpins) {
      setActiveTab(ModalGameEditTabs.main);
    }
  }, [payload?.hasFreeSpins]);

  useEffect(() => {
    let cancelled = false;
    setLoadResult(undefined);
    if (entityId !== null) {
      if (searchParams.get("tab") !== null) {
        setActiveTab(searchParams.get("tab") as ModalGameEditTabs);
      }
      dispatch(getGamesItem(entityId)).then((result) => {
        if (!cancelled) {
          setLoadResult({
            id: entityId,
            failed:
              !getGamesItem.fulfilled.match(result) ||
              !result.payload ||
              String(result.payload.id) !== entityId,
          });
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [searchParams, dispatch, entityId, loadAttempt]);

  const isLoaded = () => {
    return Boolean(
      entityId &&
        loadResult?.id === entityId &&
        !loadResult.failed &&
        isFetched &&
        payload &&
        String(payload.id) === entityId,
    );
  };

  const loaded = isLoaded();

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearItem());
  };

  const handleTabLoadingChange = useCallback((isLoading: boolean) => {
    setIsTabLoading(isLoading);
  }, []);

  const renderTab = () => {
    if (activeTab === ModalGameEditTabs.main) {
      return (
        <GamesMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          canBeUpdated={permissions?.update}
        />
      );
    }
    if (activeTab === ModalGameEditTabs.geo) {
      return (
        <GamesGeoTab
          canBeUpdated={permissions?.update}
          onLoadingChange={handleTabLoadingChange}
        />
      );
    }
    if (activeTab === ModalGameEditTabs.freeSpins) {
      return (
        <GamesFSTab
          id={payload?.id ?? ""}
          canBeUpdated={permissions?.update}
          onLoadingChange={handleTabLoadingChange}
        />
      );
    }
    if (activeTab === ModalGameEditTabs.seo) {
      return (
        <SeoTab
          currentData={data}
          onInitialData={setInitialSeoData}
          handleData={(e: any) => {
            setData(e);
          }}
          id={entityId ?? undefined}
          type="game"
          isReadOnly={!permissions?.update}
          onLoadingChange={handleTabLoadingChange}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
        />
      );
    }
    if (activeTab === ModalGameEditTabs.trail) {
      return <GamesTrailTab />;
    }
    return null;
  };

  const handleSetSeo = async () => {
    if (data) {
      setIsFetching(true);
      try {
        const attributes = getChangedSeoAttributes(initialSeoData, data, [
          "headTitle",
          "metaTitle",
          "metaDescription",
          "ogTitle",
          "ogDescription",
          "twitterTitle",
          "twitterDescription",
          "description",
          "h1",
        ]);
        const fullDescription = getChangedSeoAttributes(
          initialSeoData,
          data,
          ["fullDescription"],
          { fullDescription: normalizeSeoHtml },
        );

        await instance.post(apiRoutes.setSeoAtrs, {
          instanceId: entityId,
          instanceType: "game",
          locale: data.locale,
          attributes,
          ...fullDescription,
        });
        setInitialSeoData(data);
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        Object.entries(getSeoFieldErrors(error)).forEach(([field, code]) => {
          formErrors.setFieldError(field, code);
        });
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityUpdateError"),
            type: NotificationTypes.error,
          }),
        );
      }
      setIsFetching(false);
    }
  };

  const handleApply = async () => {
    if (activeTab === ModalGameEditTabs.main && payload) {
      formErrors.resetErrors();
      setIsFetching(true);

      try {
        await dispatch(updateGame(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      } finally {
        setIsFetching(false);
      }
      return;
    }

    if (activeTab === ModalGameEditTabs.seo && data) {
      handleSetSeo();
    }
  };

  const isDisabled = () => {
    if (isFetching) {
      return true;
    }
    if (activeTab === ModalGameEditTabs.seo && !data) {
      return true;
    }
    return false;
  };

  const isTabDisabled = (tab: ModalGameEditTabs) => {
    return tab === ModalGameEditTabs.freeSpins && !payload?.hasFreeSpins;
  };
  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("gameEditing")}</Modal.Header>

      {entityId === null ||
      (loadResult?.id === entityId && loadResult.failed) ? (
        <Modal.Content>
          <p role="alert">{t("Unable to load the game. Please try again.")}</p>
          <Button
            txt={t("Retry")}
            handleClick={() => setLoadAttempt((attempt) => attempt + 1)}
          />
        </Modal.Content>
      ) : !loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <div className="game-modal-tabs">
            {tabs.map((el) => {
              const disabled = isTabDisabled(el);

              return (
                <div
                  className={`tab-item ${activeTab === el ? "active" : ""} ${
                    disabled ? "disabled" : ""
                  }`}
                  key={el}
                  onClick={() => {
                    if (disabled) {
                      dispatch(
                        showNotification({
                          title: t("warning"),
                          message: t("enableFreeSpinsFirst"),
                          type: NotificationTypes.warning,
                        }),
                      );
                      return;
                    }

                    setActiveTab(el);
                    setIsTabLoading(
                      el === ModalGameEditTabs.geo ||
                        el === ModalGameEditTabs.seo ||
                        el === ModalGameEditTabs.freeSpins,
                    );
                  }}
                >
                  {el === ModalGameEditTabs.freeSpins && (
                    <img
                      src={lockIcon}
                      alt="locked"
                      className={`tab-lock-icon ${disabled ? "visible" : ""}`}
                    />
                  )}

                  <span className="tab-label">{t(el)}</span>
                </div>
              );
            })}
          </div>

          <Modal.Content
            className={`${styles.gameCenterGamesContentWrapper} ${
              activeTab === ModalGameEditTabs.geo ? styles.geoContent : ""
            }`}
          >
            {renderTab()}
          </Modal.Content>

          {permissions?.update && activeTab !== ModalGameEditTabs.trail && (
            <Modal.Footer>
              {isTabLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  <Button
                    disabled={isDisabled()}
                    isPrimary
                    handleClick={handleApply}
                    txt={t("save")}
                    isLoad={isFetching}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default GameCenterGamesEditModal;
